import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, ArrowsClockwise } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { syncConstraintsToDB } from '../components/MyPrediction';
import { ExportButton } from '../components/ExportCSV';
import BacktestPanel from '../components/BacktestPanel';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BiasBadge = ({ label }) => {
  if (label === 'BOOST') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800">BOOST</span>;
  if (label === 'PENALIZE') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800">PENALIZE</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">NEUTRAL</span>;
};

export default function Window24RangeBias() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [myPattern, setMyPattern] = useState('');
  const [patApplied, setPatApplied] = useState(false);
  const [rangeSet, setRangeSet] = useState(1);
  const [dbSets, setDbSets] = useState({});
  const [lastDrawNum, setLastDrawNum] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollRef = useRef(null);

  const handleRangeSetChange = (s) => {
    setRangeSet(s);
    const saved = dbSets[`C${s}`]?.range_pattern || '';
    setMyPattern(saved);
    setPatApplied(!!saved);
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsRefreshing(true);
    try {
      const [biasRes, csRes] = await Promise.all([
        axios.get(`${API}/analysis/range-bias`),
        axios.get(`${API}/analysis/constraint-sets`),
      ]);
      setData(biasRes.data);
      const cs = csRes.data || {};
      setDbSets(cs);
      if (!silent) {
        const c1Range = cs.C1?.range_pattern || '';
        setMyPattern(c1Range);
        setPatApplied(!!c1Range);
      }
      setLastUpdated(new Date());
      const latestDraw = biasRes.data?.total_draws;
      if (latestDraw) setLastDrawNum(latestDraw);
    } catch { if (!silent) toast.error('Failed to fetch range bias'); }
    finally { setLoading(false); setIsRefreshing(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  // Poll for new draws every 60 seconds
  useEffect(() => {
    const checkForNewDraw = async () => {
      try {
        const res = await axios.get(`${API}/draws/stats`);
        const latestDraw = res.data?.total_draws || res.data?.latest_draw;
        if (latestDraw && lastDrawNum && latestDraw > lastDrawNum) {
          toast.info('New draw detected! Refreshing W24 analysis...', { duration: 3000 });
          fetchData(true);
        }
      } catch {}
    };
    pollRef.current = setInterval(checkForNewDraw, 60000);
    return () => clearInterval(pollRef.current);
  }, [lastDrawNum, fetchData]);

  // Refresh on page focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && lastDrawNum) {
        axios.get(`${API}/draws/stats`).then(res => {
          const latestDraw = res.data?.total_draws || res.data?.latest_draw;
          if (latestDraw && latestDraw > lastDrawNum) {
            toast.info('New draw detected! Refreshing W24 analysis...', { duration: 3000 });
            fetchData(true);
          }
        }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [lastDrawNum, fetchData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 24</h1><p className="text-xs text-gray-500">Ball Position (1-1-1-1-1) — 5-Range Distribution Pattern Predictor</p></div>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && <span className="text-[10px] text-gray-400">Updated {lastUpdated.toLocaleTimeString()}</span>}
              <Button variant="ghost" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing} data-testid="refresh-w24-btn" className="text-gray-500 hover:text-[#002FA7]">
                <ArrowsClockwise size={18} className={isRefreshing ? 'animate-spin' : ''} />
              </Button>
              <ExportButton data={data?.range_stats} filename="w24_range_bias" />
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W24" {...NARRATIVES.W24} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="convergence" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap h-auto">
              <TabsTrigger value="convergence" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-convergence">🎯 Convergence <span className="text-[10px] font-normal opacity-70">(F1∩F2)</span></TabsTrigger>
              <TabsTrigger value="ranges" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-ranges">Range Stats <span className="text-[10px] font-normal opacity-70">(1 block)</span></TabsTrigger>
              <TabsTrigger value="patterns" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-patterns">Distribution Patterns <span className="text-[10px] font-normal opacity-70">(3 blocks)</span></TabsTrigger>
              <TabsTrigger value="predictions" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-predictions">Pattern Predictions <span className="text-[10px] font-normal opacity-70">(3 blocks)</span></TabsTrigger>
              <TabsTrigger value="recent" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recent">2026 Draws <span className="text-[10px] font-normal opacity-70">(1 block)</span></TabsTrigger>
              <TabsTrigger value="transitions" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-transitions">Range Transitions <span className="text-[10px] font-normal opacity-70">(1 block)</span></TabsTrigger>
              <TabsTrigger value="yearly-matrix" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-yearly-matrix">Yearly Matrix <span className="text-[10px] font-normal opacity-70">(per ball × range)</span></TabsTrigger>
            </TabsList>

            <TabsContent value="convergence">
              <W24ConvergenceTab data={data} />
            </TabsContent>

            <TabsContent value="ranges">
              <div className="bg-white border rounded p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-1">Block 1 — Range Distribution</h3>
                <p className="text-xs text-gray-500 mb-3">Till 2025 ({data.total_2025} draws) vs 2026 ({data.total_2026} draws) — Overall {data.total_draws} draws</p>
                <div className="overflow-x-auto" data-testid="range-stats-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold">Range</th>
                        <th className="px-3 py-3 text-center bg-blue-50">Till 2025</th>
                        <th className="px-3 py-3 text-center bg-blue-50">%</th>
                        <th className="px-3 py-3 text-center bg-amber-50 font-bold">2026</th>
                        <th className="px-3 py-3 text-center bg-amber-50 font-bold">%</th>
                        <th className="px-3 py-3 text-center">% Diff</th>
                        <th className="px-3 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.range_stats.map((rs, idx) => (
                        <tr key={rs.range} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`range-row-${rs.range}`}>
                          <td className="px-4 py-3 border-b font-mono font-bold text-lg">{rs.range}</td>
                          <td className="px-3 py-3 border-b font-mono text-center bg-blue-50/30">{rs.total_2025}</td>
                          <td className="px-3 py-3 border-b font-mono text-center bg-blue-50/30">{rs.pct_2025}%</td>
                          <td className="px-3 py-3 border-b font-mono text-center font-bold bg-amber-50/30">{rs.total_2026}</td>
                          <td className="px-3 py-3 border-b font-mono text-center font-bold bg-amber-50/30">{rs.pct_2026}%</td>
                          <td className={`px-3 py-3 border-b font-mono text-center font-bold ${rs.pct_diff > 0 ? 'text-emerald-600 bg-emerald-50' : rs.pct_diff < 0 ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>
                            {rs.pct_diff > 0 ? '+' : ''}{rs.pct_diff}%
                          </td>
                          <td className="px-3 py-3 border-b text-center"><BiasBadge label={rs.pred_label} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="patterns">
              <div className="space-y-6">
                {/* Point 3: % Bucket Breakdown — Overall vs 2026 */}
                <div className="bg-white border rounded p-4">
                  <div className="bg-gradient-to-r from-[#002FA7] to-indigo-600 rounded-lg px-4 py-3 mb-4 text-white">
                    <h3 className="font-bold text-lg">Block 1 — Pattern Spread Analysis: Overall % Buckets vs 2026</h3>
                    <p className="text-xs text-white/80">How many patterns in each frequency bucket (by Overall %) appeared in 2026 — shows which frequency tier the 2026 draws are clustering in</p>
                  </div>

                  {(() => {
                    // Build % buckets from all patterns
                    const allPats = [];
                    (data.pct_range_analysis || []).forEach(b => {
                      b.patterns.forEach(p => allPats.push(p));
                    });
                    // Also include patterns_in_2026 that might not be in pct_range_analysis
                    (data.patterns_in_2026 || []).forEach(p => {
                      if (!allPats.find(a => a.pattern === p.pattern)) {
                        allPats.push({ ...p, count_overall: p.count_2025, pct_overall: p.pct_2025 });
                      }
                    });

                    const buckets = [
                      { label: '0% - 0.5%', min: 0, max: 0.5 },
                      { label: '0.51% - 1.0%', min: 0.51, max: 1.0 },
                      { label: '1.01% - 2.0%', min: 1.01, max: 2.0 },
                      { label: '2.01% - 3.0%', min: 2.01, max: 3.0 },
                      { label: '3.01% - 5.0%', min: 3.01, max: 5.0 },
                      { label: '5.01%+', min: 5.01, max: 100 },
                    ];

                    const bucketData = buckets.map(bk => {
                      const pats = allPats.filter(p => {
                        const pct = p.pct_overall || p.pct_2025 || 0;
                        return pct >= bk.min && pct <= bk.max;
                      });
                      const hitIn2026 = pats.filter(p => p.appeared_2026 || (p.count_2026 && p.count_2026 > 0));
                      const totalCount2026 = hitIn2026.reduce((sum, p) => sum + (p.count_2026 || 0), 0);
                      return {
                        ...bk,
                        totalPatterns: pats.length,
                        hit2026: hitIn2026.length,
                        miss2026: pats.length - hitIn2026.length,
                        totalDraws2026: totalCount2026,
                        coveragePct: pats.length > 0 ? Math.round(hitIn2026.length / pats.length * 100) : 0,
                        patterns: pats.sort((a, b) => (b.pct_overall || b.pct_2025 || 0) - (a.pct_overall || a.pct_2025 || 0)),
                      };
                    });

                    return (
                      <div>
                        {/* Summary grid */}
                        <div className="grid grid-cols-6 gap-3 mb-5">
                          {bucketData.map(bk => (
                            <div key={bk.label} className={`border rounded-lg p-3 text-center ${bk.hit2026 > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="font-bold text-sm text-[#002FA7] mb-1">{bk.label}</div>
                              <div className="text-xs text-gray-500 mb-2">{bk.totalPatterns} patterns overall</div>
                              <div className="flex justify-center gap-3 text-xs mb-1">
                                <span className="text-emerald-600 font-bold">{bk.hit2026} hit</span>
                                <span className="text-red-500 font-bold">{bk.miss2026} miss</span>
                              </div>
                              <div className="text-xs font-bold text-amber-700">{bk.totalDraws2026} draws in 2026</div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${bk.coveragePct}%` }}></div>
                              </div>
                              <div className="mt-1 text-[10px] text-gray-400">{bk.coveragePct}% coverage</div>
                            </div>
                          ))}
                        </div>

                        {/* Detailed table per bucket */}
                        <div className="space-y-4">
                          {bucketData.filter(bk => bk.totalPatterns > 0).map((bk, idx) => (
                            <div key={bk.label} className="border rounded-lg overflow-hidden">
                              <div className={`flex items-center justify-between px-4 py-2 ${idx % 2 === 0 ? 'bg-[#002FA7]/5' : 'bg-gray-50'} border-b`}>
                                <div className="flex items-center gap-4">
                                  <span className="font-bold text-[#002FA7] text-sm">{bk.label}</span>
                                  <span className="font-mono font-bold">{bk.totalPatterns} patterns</span>
                                </div>
                                <div className="flex items-center gap-4 text-xs">
                                  <span className="text-emerald-600 font-bold">{bk.hit2026} hit in 2026</span>
                                  <span className="text-red-500 font-bold">{bk.miss2026} miss</span>
                                  <span className="text-amber-700 font-bold">{bk.totalDraws2026} draws in 2026</span>
                                </div>
                              </div>
                              <table className="w-full text-xs">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-1.5 text-left uppercase">Pattern</th>
                                    <th className="px-3 py-1.5 text-center uppercase bg-blue-50">Overall Count</th>
                                    <th className="px-3 py-1.5 text-center uppercase bg-blue-50">Overall %</th>
                                    <th className="px-3 py-1.5 text-center uppercase bg-amber-50">2026 Count</th>
                                    <th className="px-3 py-1.5 text-center uppercase bg-amber-50">2026 %</th>
                                    <th className="px-3 py-1.5 text-center uppercase">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bk.patterns.map((p, pi) => (
                                    <tr key={p.pattern} className={p.appeared_2026 || p.count_2026 > 0 ? 'bg-emerald-50/40' : pi % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                      <td className="px-3 py-1 border-b font-mono font-bold">{p.pattern}</td>
                                      <td className="px-3 py-1 border-b font-mono text-center">{p.count_overall || p.count_2025}</td>
                                      <td className="px-3 py-1 border-b font-mono text-center">{p.pct_overall || p.pct_2025}%</td>
                                      <td className="px-3 py-1 border-b font-mono text-center font-bold">{p.count_2026 || '-'}</td>
                                      <td className="px-3 py-1 border-b font-mono text-center font-bold">{p.pct_2026 ? `${p.pct_2026}%` : '-'}</td>
                                      <td className="px-3 py-1 border-b text-center">
                                        {(p.appeared_2026 || p.count_2026 > 0)
                                          ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">HIT</span>
                                          : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">MISS</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Point 2: ALL patterns sorted by Overall % descending */}
                <div className="bg-white border rounded p-4">
                  <h3 className="font-bold text-gray-900 mb-1">Block 2 — All Distribution Patterns — Overall % (Highest First)</h3>
                  <p className="text-xs text-gray-500 mb-3">All {data.total_draws} draws — sorted by overall frequency descending. Shows 2026 appearance alongside.</p>
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs uppercase w-10">#</th>
                          <th className="px-4 py-2 text-left text-xs uppercase">Pattern</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">Overall Count</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">Overall %</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-amber-50">2026 Count</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-amber-50">2026 %</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Merge all patterns from top_patterns + patterns_in_2026 + missing
                          const allMap = {};
                          (data.top_patterns || []).forEach(p => {
                            allMap[p.pattern] = { ...allMap[p.pattern], pattern: p.pattern, count_overall: p.count, pct_overall: p.pct };
                          });
                          (data.patterns_in_2026 || []).forEach(p => {
                            allMap[p.pattern] = { ...allMap[p.pattern], pattern: p.pattern, count_overall: p.count_2025, pct_overall: p.pct_2025, count_2026: p.count_2026, pct_2026: p.pct_2026 };
                          });
                          (data.missing_in_2026 || []).forEach(p => {
                            allMap[p.pattern] = { ...allMap[p.pattern], pattern: p.pattern, count_overall: p.count_overall, pct_overall: p.pct_overall, count_2026: 0, pct_2026: 0 };
                          });
                          // Also from pct_range_analysis
                          (data.pct_range_analysis || []).forEach(b => {
                            b.patterns.forEach(p => {
                              if (!allMap[p.pattern]) {
                                allMap[p.pattern] = { pattern: p.pattern, count_overall: p.count_overall || p.count_2025, pct_overall: p.pct_overall || p.pct_2025, count_2026: p.count_2026, pct_2026: p.pct_2026 };
                              }
                            });
                          });
                          const sorted = Object.values(allMap).sort((a, b) => (b.pct_overall || 0) - (a.pct_overall || 0));
                          return sorted.map((p, idx) => (
                            <tr key={p.pattern} className={`${p.count_2026 > 0 ? 'bg-emerald-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <td className="px-3 py-1.5 border-b text-gray-400 text-xs">{idx + 1}</td>
                              <td className="px-4 py-1.5 border-b font-mono font-bold">{p.pattern}</td>
                              <td className="px-3 py-1.5 border-b font-mono text-center">{p.count_overall || 0}</td>
                              <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{p.pct_overall || 0}%</td>
                              <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{p.count_2026 || '-'}</td>
                              <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{p.pct_2026 ? `${p.pct_2026}%` : '-'}</td>
                              <td className="px-3 py-1.5 border-b text-center">
                                {p.count_2026 > 0
                                  ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">HIT</span>
                                  : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">MISS</span>}
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Missing in 2026 */}
                <div className="bg-white border rounded p-4">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg px-4 py-3 mb-4 text-white">
                    <h3 className="font-bold text-lg">Block 3 — Top Distribution Patterns Missing in 2026</h3>
                    <p className="text-xs text-white/80">Patterns that appeared overall/till 2025 but have NOT appeared in any 2026 draw — sorted by overall % (highest first)</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs uppercase">#</th>
                          <th className="px-4 py-2 text-left text-xs uppercase">Pattern</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Overall Count</th>
                          <th className="px-3 py-2 text-center text-xs uppercase">Overall %</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">Till 2025 Count</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-blue-50">Till 2025 %</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-red-50">2026</th>
                          <th className="px-3 py-2 text-center text-xs uppercase bg-red-50">Missing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.missing_in_2026 || []).map((p, idx) => (
                          <tr key={p.pattern} className={idx < 5 ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx < 5 ? { borderLeft: '3px solid #ef4444' } : {}}>
                            <td className="px-4 py-2 border-b font-mono text-gray-400">{idx + 1}</td>
                            <td className="px-4 py-2 border-b font-mono font-bold">{p.pattern}</td>
                            <td className="px-3 py-2 border-b font-mono text-center">{p.count_overall}</td>
                            <td className="px-3 py-2 border-b font-mono text-center font-bold">{p.pct_overall}%</td>
                            <td className="px-3 py-2 border-b font-mono text-center bg-blue-50/30">{p.count_2025}</td>
                            <td className="px-3 py-2 border-b font-mono text-center bg-blue-50/30">{p.pct_2025}%</td>
                            <td className="px-3 py-2 border-b font-mono text-center font-bold text-red-600 bg-red-50">MISSING</td>
                            {idx < 5 && <td className="px-3 py-2 border-b font-mono text-center font-bold text-red-700 bg-red-100">{p.missing_draws} draws</td>}
                            {idx >= 5 && <td className="px-3 py-2 border-b font-mono text-center text-gray-400">{p.missing_draws}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </TabsContent>

            <TabsContent value="predictions">
              <div className="space-y-6">
                {/* My Range Pattern Prediction */}
                <div className={`rounded-lg p-5 text-white transition-all ${patApplied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-range">
                  {/* C1/C2/C3 selector */}
                  <div className="flex items-center gap-1.5 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => handleRangeSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${rangeSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : !!dbSets[`C${s}`]?.range_pattern ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`range-set-${s}`}>C{s}</button>
                    ))}
                    <span className="text-[10px] text-white/40 ml-2">5 independent sets</span>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg">Block 1 — My Range Pattern Prediction</h3>
                      <p className="text-xs text-white/70">Set 1 or more distribution patterns (OR logic) — applies across prediction windows</p>
                    </div>
                    {patApplied ? (
                      <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
                        <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
                      </span>
                    ) : (
                      <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
                    )}
                  </div>

                  {/* C1-C5 overview strip */}
                  <div className="flex flex-wrap gap-2 mb-3 bg-white/10 rounded-lg px-4 py-2.5">
                    {[1,2,3,4,5].map(s => {
                      const val = dbSets[`C${s}`]?.range_pattern;
                      return (
                        <div key={s} className={`flex items-center gap-1.5 text-xs ${rangeSet === s ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                          <span className={`font-bold ${val ? 'text-emerald-300' : 'text-white/30'}`}>C{s}:</span>
                          {val ? (
                            <span className="font-mono font-bold text-white/90">{val.split('|').join(' | ')}</span>
                          ) : (
                            <span className="text-white/30 italic">not set</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Current patterns as chips */}
                  {(() => {
                    const patterns = myPattern.split('|').filter(Boolean);
                    const addPattern = (val) => {
                      if (!val) { toast.error('Enter a pattern first'); return; }
                      const trimmed = val.trim();
                      const parts = trimmed.split('-');
                      if (parts.length !== 5) { toast.error(`Pattern must have 5 numbers (L-ML-M-MH-H). Got ${parts.length}.`); return; }
                      const nums = parts.map(Number);
                      if (nums.some(isNaN)) { toast.error('Pattern must contain only numbers separated by dashes'); return; }
                      if (nums.reduce((a, b) => a + b, 0) !== 5) { toast.error(`Numbers must sum to 5 (got ${nums.reduce((a, b) => a + b, 0)})`); return; }
                      if (patterns.includes(trimmed)) { toast.error('Pattern already added'); return; }
                      const newPats = [...patterns, trimmed].join('|');
                      setMyPattern(newPats);
                      setPatApplied(false);
                    };
                    return (
                      <>
                        {patterns.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {patterns.map((pat, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-lg text-sm font-mono font-bold ring-1 ring-white/30">
                                {pat}
                                <button onClick={() => {
                                  const newPats = patterns.filter((_, i) => i !== idx).join('|');
                                  setMyPattern(newPats);
                                  setPatApplied(false);
                                }} className="text-red-300 hover:text-red-100 font-bold ml-1" data-testid={`remove-pat-${idx}`}>&times;</button>
                              </span>
                            ))}
                            {patterns.length > 1 && <span className="text-[10px] text-white/50 self-center ml-1">OR logic — matches any</span>}
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-bold opacity-80">{patterns.length > 0 ? 'Add:' : 'Pattern:'}</label>
                            <input type="text" defaultValue=""
                              placeholder={patterns.length > 0 ? 'e.g. 0-2-1-1-1' : 'e.g. 2-1-0-1-1'}
                              className="w-48 px-2 py-1.5 rounded text-gray-900 font-mono font-bold text-center" data-testid="range-pattern-input"
                              ref={el => { if (el) el._rangeRef = true; }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  addPattern(e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <button onClick={(e) => {
                              const inp = e.target.closest('.flex').querySelector('input');
                              if (inp) {
                                addPattern(inp.value);
                                inp.value = '';
                              }
                            }} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded font-bold text-sm transition" data-testid="add-range-btn">+</button>
                          </div>
                          <p className="text-[9px] text-white/50">Format: L-ML-M-MH-H (must sum to 5)</p>
                          {/* Quick pick from top predictions */}
                          <div className="flex items-center gap-1">
                            {(data?.pattern_predictions || []).slice(0, 3).map(p => (
                              <button key={p.pattern} onClick={() => {
                                if (!patterns.includes(p.pattern)) {
                                  const newPats = [...patterns, p.pattern].join('|');
                                  setMyPattern(newPats);
                                  setPatApplied(false);
                                }
                              }}
                                className={`px-2 py-1 rounded text-[10px] font-mono font-bold transition-colors ${patterns.includes(p.pattern) ? 'bg-emerald-400/40 text-emerald-100 ring-1 ring-emerald-300' : 'bg-white/15 hover:bg-white/25'}`}
                                data-testid={`quick-pick-${p.pattern}`}>
                                {p.pattern} {patterns.includes(p.pattern) && '✓'}
                              </button>
                            ))}
                          </div>
                          <Button onClick={() => {
                            setPatApplied(true); setDbSets(prev => ({ ...prev, [`C${rangeSet}`]: { ...prev[`C${rangeSet}`], range_pattern: myPattern } })); syncConstraintsToDB(rangeSet, { range_pattern: myPattern }); toast.success(`Range pattern C${rangeSet} applied: ${myPattern}`); }}
                            className={`font-bold transition-all ${patApplied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="save-range-pattern-btn">
                            {patApplied ? 'Applied' : 'Save Constraint'}
                          </Button>
                          {patApplied && (
                            <Button onClick={() => { setPatApplied(false); setMyPattern(''); setDbSets(prev => ({ ...prev, [`C${rangeSet}`]: { ...prev[`C${rangeSet}`], range_pattern: null } })); syncConstraintsToDB(rangeSet, { range_pattern: null }); toast.info(`Range pattern C${rangeSet} cleared`); }}
                              variant="ghost" className="text-white/70 hover:text-white text-xs">Clear All</Button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  {patApplied && myPattern && (() => {
                    const patterns = myPattern.split('|').filter(Boolean);
                    return (
                      <div className="mt-3 space-y-1">
                        {patterns.map((pat, idx) => {
                          const match = (data?.pattern_predictions || []).find(p => p.pattern === pat);
                          return match ? (
                            <div key={idx} className="flex items-center gap-4 bg-white/10 rounded px-3 py-2">
                              <span className="font-mono font-bold text-lg">{match.pattern}</span>
                              <span className="text-xs">Score: <b>{match.pred_score}</b></span>
                              <span className="text-xs">Freq: <b>{match.freq_pct}%</b></span>
                              <span className="text-xs">Gap: <b>{match.current_gap}</b></span>
                              <span className="text-xs">Overdue: <b>{match.overdue_ratio}x</b></span>
                            </div>
                          ) : (
                            <div key={idx} className="bg-white/10 rounded px-3 py-2 text-xs opacity-70">Pattern "{pat}" — custom entry</div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Top Prediction Banner */}
                {/* Transition-Based Prediction — above Predicted Next Distribution Patterns */}
                {data.transition_prediction && (
                  <div className="bg-white border-2 border-purple-300 rounded-xl p-5 shadow-sm" data-testid="transition-prediction">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg px-4 py-3 mb-4 text-white">
                      <h3 className="font-bold text-lg">Block 2 — Transition-Based Next Draw Prediction</h3>
                      <p className="text-xs text-white/80">Based on Range Transition Matrix — top probable next values per range, with all valid combinations (sum=5)</p>
                    </div>

                    <div className="grid grid-cols-5 gap-3 mb-5">
                      {['L', 'ML', 'M', 'MH', 'H'].map(rl => {
                        const t = data.transition_prediction[rl];
                        if (!t) return null;
                        const rangeLabel = {L:'1-10', ML:'11-20', M:'21-30', MH:'31-40', H:'41-50'}[rl];
                        return (
                          <div key={rl} className="border-2 border-purple-200 rounded-lg p-3 bg-purple-50/30" data-testid={`tp-${rl}`}>
                            <div className="text-center mb-2">
                              <span className="font-bold text-lg text-purple-700">{rl}</span>
                              <span className="text-xs text-gray-500 ml-1">({rangeLabel})</span>
                            </div>
                            <div className="text-center mb-2">
                              <span className="text-xs text-gray-400">Current:</span>
                              <span className="bg-purple-600 text-white font-bold px-2 py-0.5 rounded-full text-sm ml-1">{t.current}</span>
                            </div>
                            <div className="text-center mb-2">
                              <span className="text-xs text-gray-400">Predicted:</span>
                              <div className="flex justify-center gap-1 mt-1">
                                {t.predicted_values.map((v, i) => (
                                  <span key={v} className={`font-bold px-2 py-0.5 rounded text-sm ${
                                    i === 0 ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                                    i === 1 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    'bg-gray-100 text-gray-600 border border-gray-200'
                                  }`}>{v}</span>
                                ))}
                              </div>
                            </div>
                            <div className="text-center text-[10px] space-y-0.5">
                              <div>Top2 Overall: <span className="font-bold text-purple-700">{t.combined_pct_overall}%</span></div>
                              <div>Top2 2026: <span className="font-bold text-blue-600">{t.combined_pct_2026}%</span></div>
                            </div>
                            <div className={`mt-2 text-center text-[9px] px-2 py-1 rounded ${
                              t.combined_pct_overall >= 70 ? 'bg-emerald-100 text-emerald-700' :
                              t.combined_pct_overall >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {t.remark}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {data.transition_combos && data.transition_combos.length > 0 && (
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          Primary Prediction ({data.transition_combos.length})
                          <span className="text-xs font-normal text-gray-400">— top 2 values per range (highest Overall %)</span>
                        </h4>
                        <div className="overflow-x-auto mb-4">
                          <table className="w-full text-sm">
                            <thead className="bg-purple-100">
                              <tr>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700">#</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-purple-700">Pattern (L-ML-M-MH-H)</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700 bg-blue-50">Overall Count</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700 bg-blue-50">Overall %</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700 bg-amber-50">2026 Count</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700 bg-amber-50">2026 %</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-purple-700">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.transition_combos.map((c, idx) => (
                                <tr key={c.pattern} className="bg-purple-50" style={{ borderLeft: '3px solid #7c3aed' }}>
                                  <td className="px-3 py-2 border-b text-center text-purple-500 font-mono">{idx + 1}</td>
                                  <td className="px-4 py-2 border-b font-mono font-bold text-xl text-purple-800">{c.pattern}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.count_overall}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold text-purple-700">{c.pct_overall}%</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.count_2026 || '-'}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.pct_2026 ? `${c.pct_2026}%` : '-'}</td>
                                  <td className="px-3 py-2 border-b text-center">
                                    {c.count_2026 > 0
                                      ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">HIT in 2026</span>
                                      : <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Not in 2026</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {data.extended_combos && data.extended_combos.length > 0 && (
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          Extended Combinations ({data.extended_combos.length})
                          <span className="text-xs font-normal text-gray-400">— includes 3rd probable value (=2) per range for more variety</span>
                        </h4>
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500">#</th>
                                <th className="px-4 py-2 text-left text-xs font-bold text-gray-500">Pattern (L-ML-M-MH-H)</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500 bg-blue-50">Overall Count</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500 bg-blue-50">Overall %</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500 bg-amber-50">2026 Count</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500 bg-amber-50">2026 %</th>
                                <th className="px-3 py-2 text-center text-xs font-bold text-gray-500">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.extended_combos.map((c, idx) => (
                                <tr key={c.pattern} className={`${c.count_2026 > 0 ? 'bg-emerald-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                  style={c.count_2026 > 0 ? { borderLeft: '3px solid #10b981' } : {}}>
                                  <td className="px-3 py-2 border-b text-center text-gray-400 font-mono">{idx + 1}</td>
                                  <td className="px-4 py-2 border-b font-mono font-bold text-lg">{c.pattern}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono">{c.count_overall}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.pct_overall}%</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.count_2026 || '-'}</td>
                                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{c.pct_2026 ? `${c.pct_2026}%` : '-'}</td>
                                  <td className="px-3 py-2 border-b text-center">
                                    {c.count_overall === 0
                                      ? <span className="text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">NEVER</span>
                                      : c.count_2026 > 0
                                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">HIT in 2026</span>
                                        : <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Not in 2026</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                      <span className="font-bold">Summary:</span> Primary: <strong>{data.transition_combos?.[0]?.pattern || '-'}</strong> ({data.transition_combos?.[0]?.pct_overall || 0}% overall).
                      {' '}Extended: {data.extended_combos?.length || 0} additional patterns, {data.extended_combos?.filter(c => c.count_2026 > 0).length || 0} appeared in 2026.
                      {' '}Top extended: <strong>{data.extended_combos?.[0]?.pattern || '-'}</strong> ({data.extended_combos?.[0]?.pct_overall || 0}% overall{data.extended_combos?.[0]?.count_2026 > 0 ? `, ${data.extended_combos[0].count_2026}x in 2026` : ''}).
                    </div>
                  </div>
                )}

                {/* Full table */}
                <div className="bg-white border rounded p-4">
                  <h3 className="font-bold text-gray-900 mb-1">Block 3 — All Patterns Ranked by Combined Score</h3>
                  <p className="text-xs text-gray-500 mb-1">Combined = Prediction Score × (Reach / 10) × Sign Boost — Last draw [{(data.last_draw_vals || []).join(', ')}]</p>
                  <p className="text-[10px] text-gray-400 mb-3">Reach = per-ball directional diff coverage. Sign = W3 sign pattern probability (direction each ball must move). Higher combined = more likely AND more reachable.</p>
                  <div className="overflow-x-auto" data-testid="pattern-predictions-table">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-2 text-center text-xs uppercase">#</th>
                          <th className="px-3 py-2 text-left text-xs uppercase">Pattern</th>
                          <th className="px-2 py-2 text-center text-xs uppercase">Freq %</th>
                          <th className="px-2 py-2 text-center text-xs uppercase">Gap</th>
                          <th className="px-2 py-2 text-center text-xs uppercase">Overdue</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-amber-50">Pred</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-blue-50">F1</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-orange-50">F2</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-emerald-50">Conv</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-indigo-50">Trans%</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-yellow-50">Drift</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-pink-50">Bucket</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-teal-50">Reach</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-cyan-50">R-2026</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-purple-100 font-bold">Combined</th>
                          <th className="px-2 py-2 text-center text-xs uppercase bg-green-50">Sign%</th>
                          <th className="px-2 py-2 text-center text-xs uppercase">Per-Ball Fit</th>
                          <th className="px-2 py-2 text-center text-xs uppercase">2026</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.pattern_predictions || []).map((p, idx) => (
                          <tr key={p.pattern} className={`${p.blocked ? 'opacity-50' : idx < 5 ? 'bg-amber-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                            style={!p.blocked && idx < 5 ? { borderLeft: '3px solid #f59e0b' } : p.blocked ? { borderLeft: '3px solid #ef4444' } : {}}>
                            <td className="px-2 py-2 border-b font-mono text-center text-gray-400">{idx + 1}</td>
                            <td className="px-3 py-2 border-b font-mono font-bold text-lg">{p.pattern}</td>
                            <td className="px-2 py-2 border-b font-mono text-center">{p.freq_pct}%</td>
                            <td className={`px-2 py-2 border-b font-mono text-center font-bold ${p.overdue_ratio >= 2 ? 'text-red-600' : p.overdue_ratio >= 1 ? 'text-amber-600' : 'text-gray-600'}`}>{p.current_gap}</td>
                            <td className={`px-2 py-2 border-b font-mono text-center font-bold ${p.overdue_ratio >= 2 ? 'text-red-600 bg-red-50' : p.overdue_ratio >= 1 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600'}`}>{p.overdue_ratio}x</td>
                            <td className="px-2 py-2 border-b font-mono text-center font-bold bg-amber-50/30 text-[#002FA7]">{p.pred_score}</td>
                            <td className="px-2 py-2 border-b font-mono text-center font-bold bg-blue-50/30 text-blue-700">{p.f1_score ?? '—'}</td>
                            <td className="px-2 py-2 border-b font-mono text-center font-bold bg-orange-50/30 text-orange-700">{p.f2_score ?? '—'}</td>
                            <td className="px-2 py-2 border-b text-center bg-emerald-50/30">
                              {p.convergence_tier ? (
                                <span className={`text-[9px] font-black px-1 py-0.5 rounded ${p.convergence_tier === 'ELITE' ? 'bg-amber-300 text-amber-950' : p.convergence_tier === 'STRONG' ? 'bg-emerald-200 text-emerald-900' : 'bg-gray-200 text-gray-700'}`}>{p.convergence_tier === 'ELITE' ? '🥇' : p.convergence_tier === 'STRONG' ? '🥈' : '🥉'}</span>
                              ) : <span className="text-[10px] text-gray-300">—</span>}
                            </td>
                            <td className={`px-2 py-2 border-b font-mono text-center font-bold bg-indigo-50/30 ${p.trans_match_pct >= 80 ? 'text-emerald-700' : p.trans_match_pct >= 40 ? 'text-blue-700' : 'text-gray-500'}`}>{p.trans_match_pct ?? 0}%</td>
                            <td className={`px-2 py-2 border-b font-mono text-center font-bold bg-yellow-50/30 ${p.yearly_drift > 0 ? 'text-emerald-700' : p.yearly_drift < 0 ? 'text-red-600' : 'text-gray-500'}`}>{p.yearly_drift > 0 ? '+' : ''}{p.yearly_drift ?? 0}</td>
                            <td className="px-2 py-2 border-b text-center bg-pink-50/30">
                              {p.bucket_tier ? (
                                <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${p.bucket_tier === 'SWEET' ? 'bg-emerald-100 text-emerald-700' : p.bucket_tier === 'WARM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{p.bucket_tier}</span>
                              ) : <span className="text-[10px] text-gray-300">—</span>}
                            </td>
                            <td className={`px-2 py-2 border-b font-mono text-center font-bold ${p.reach_score >= 8 ? 'text-emerald-600 bg-emerald-50' : p.reach_score >= 6 ? 'text-blue-600 bg-blue-50' : 'text-red-600 bg-red-50'}`}>{p.reach_score}</td>
                            <td className={`px-2 py-2 border-b font-mono text-center ${p.reach_2026 >= 8 ? 'text-emerald-600' : p.reach_2026 >= 6 ? 'text-blue-600' : 'text-red-600'}`}>{p.reach_2026}</td>
                            <td className="px-2 py-2 border-b font-mono text-center font-bold text-lg bg-purple-50/50 text-purple-700">{p.combined_score}</td>
                            <td className="px-2 py-2 border-b text-center">
                              <div className="font-mono text-[10px] font-bold text-gray-700">{(p.implied_signs || '').split('').map((s, si) => (
                                <span key={si} className={s === '+' ? 'text-emerald-600' : s === '-' ? 'text-red-600' : 'text-gray-400'}>{s === '+' ? '▲' : s === '-' ? '▼' : '*'}</span>
                              ))}</div>
                              <div className={`text-[9px] font-bold ${p.sign_pct >= 20 ? 'text-emerald-600' : p.sign_pct >= 10 ? 'text-blue-600' : 'text-red-500'}`}>{p.sign_pct}%</div>
                            </td>
                            <td className="px-2 py-2 border-b">
                              <div className="flex gap-0.5 justify-center">
                                {(p.ball_details || []).map((bd, bi) => (
                                  <span key={bi} className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                                    bd.fit === 'EASY' ? 'bg-emerald-100 text-emerald-700' :
                                    bd.fit === 'OK' ? 'bg-blue-100 text-blue-700' :
                                    bd.fit === 'STRETCH' ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`} title={`${bd.ball}(${bd.last})→${bd.target_range} ${bd.diff_needed} cov:${bd.cov_overall}%`}>
                                    {bd.ball.replace('B','')}:{bd.fit[0]}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-2 py-2 border-b text-center">
                              {p.appeared_2026
                                ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">{p.count_2026}×</span>
                                : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="recent">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">Block 1 — 2026 Draws — Range Patterns</h3>
                <p className="text-xs text-gray-500 mb-2">Auto-refreshes when new draws are detected (checks every 60s, also on tab focus)</p>
                <div className="overflow-x-auto" data-testid="recent-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center">Draw</th>
                        <th className="px-3 py-2 text-center">Date</th>
                        <th className="px-3 py-2 text-center">Balls</th>
                        <th className="px-3 py-2 text-center">Pattern</th>
                        <th className="px-3 py-2 text-center">1-10</th>
                        <th className="px-3 py-2 text-center">11-20</th>
                        <th className="px-3 py-2 text-center">21-30</th>
                        <th className="px-3 py-2 text-center">31-40</th>
                        <th className="px-3 py-2 text-center">41-50</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recent_draws || data.last_20 || []).map((d, idx) => (
                        <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-1.5 border-b font-mono text-center">#{d.draw}</td>
                          <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{d.balls.join(', ')}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{d.pattern}</td>
                          {d.counts.map((c, i) => (
                            <td key={i} className={`px-3 py-1.5 border-b font-mono text-center font-bold ${c >= 2 ? 'text-[#002FA7] bg-blue-50' : c === 0 ? 'text-gray-300' : ''}`}>{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="transitions">
              <div className="space-y-6">
                <div className="bg-white border rounded p-4">
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg px-4 py-3 mb-4 text-white">
                    <h3 className="font-bold text-lg">Block 1 — Range Transition Matrix — 2026 Draws</h3>
                    <p className="text-xs text-white/80">For each range bucket (L, ML, M, MH, H): when the count is X in one draw, what does it become in the next draw? Based on {data.total_2026} draws in 2026.</p>
                  </div>

                  {data.range_transitions && Object.entries(data.range_transitions).map(([key, t]) => {
                    const matrix = t.matrix || {};
                    const allFromValues = Object.keys(matrix).map(Number).sort((a, b) => a - b);
                    const allToValues = [...new Set(Object.values(matrix).flatMap(tos => Object.keys(tos).map(Number)))].sort((a, b) => a - b);
                    const allValues = [...new Set([...allFromValues, ...allToValues])].sort((a, b) => a - b);

                    return (
                      <div key={key} className="mb-8 bg-white border rounded-xl overflow-hidden shadow-sm" data-testid={`transition-${key}`}>
                        <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
                          <div>
                            <span className="font-bold text-lg text-gray-900">{key}</span>
                            <span className="text-sm text-gray-500 ml-2">({t.range})</span>
                            <span className="text-xs text-gray-400 ml-3">{t.total_transitions} transitions in 2026</span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-gray-500">Current (latest draw): </span>
                            <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded-full text-sm">{t.current_value}</span>
                          </div>
                        </div>

                        <div className="p-5">
                          {/* Transition matrix table */}
                          <div className="overflow-x-auto mb-4">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="px-3 py-2 text-left font-bold text-gray-700 border-b-2 border-gray-300">From ↓ / To →</th>
                                  {allValues.map(to => (
                                    <th key={to} className="px-3 py-2 text-center font-bold text-gray-700 border-b-2 border-gray-300 min-w-[70px]">{to}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {allFromValues.map(fr => {
                                  const tos = matrix[fr] || {};
                                  const totalFrom = Object.values(tos).reduce((sum, v) => sum + v.count, 0);
                                  const isCurrentRow = fr === t.current_value;
                                  return (
                                    <tr key={fr} className={isCurrentRow ? 'bg-purple-50 ring-2 ring-purple-300 ring-inset' : ''}>
                                      <td className={`px-3 py-2 font-bold border-b ${isCurrentRow ? 'text-purple-700 text-lg' : 'text-gray-800'}`}>
                                        {fr}
                                        {isCurrentRow && <span className="text-[10px] text-purple-500 ml-1">CURRENT</span>}
                                        <span className="text-[10px] text-gray-400 ml-2">({totalFrom}x)</span>
                                      </td>
                                      {allValues.map(to => {
                                        const cell = tos[to];
                                        if (!cell) return <td key={to} className="px-3 py-2 text-center border-b text-gray-200">-</td>;
                                        const pct = cell.pct;
                                        const isHighest = pct === Math.max(...Object.values(tos).map(v => v.pct));
                                        return (
                                          <td key={to} className={`px-3 py-2 text-center border-b ${isHighest ? 'font-bold' : ''}`}>
                                            <div className={`rounded-lg px-2 py-1.5 ${
                                              pct >= 50 ? 'bg-emerald-100 text-emerald-800' :
                                              pct >= 30 ? 'bg-blue-100 text-blue-800' :
                                              pct >= 15 ? 'bg-amber-50 text-amber-800' :
                                              'bg-gray-50 text-gray-600'
                                            }`}>
                                              <div className="font-bold text-sm">{cell.count}x</div>
                                              <div className="text-[10px]">{pct}%</div>
                                            </div>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* OVERALL transition matrix */}
                          {t.overall_matrix && (() => {
                            const oMatrix = t.overall_matrix;
                            const oFromValues = Object.keys(oMatrix).map(Number).sort((a, b) => a - b);
                            const oToValues = [...new Set(Object.values(oMatrix).flatMap(tos => Object.keys(tos).map(Number)))].sort((a, b) => a - b);
                            const oAllValues = [...new Set([...oFromValues, ...oToValues])].sort((a, b) => a - b);
                            return (
                              <div className="overflow-x-auto mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-bold text-gray-500 uppercase">Overall (All {t.total_overall_transitions} draws)</span>
                                  <span className="text-[10px] text-gray-400">— historical transition probabilities for comparison</span>
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-200">
                                      <th className="px-3 py-1.5 text-left font-bold text-gray-600 border-b border-gray-300">From ↓ / To →</th>
                                      {oAllValues.map(to => (
                                        <th key={to} className="px-3 py-1.5 text-center font-bold text-gray-600 border-b border-gray-300 min-w-[60px]">{to}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {oFromValues.map(fr => {
                                      const tos = oMatrix[fr] || {};
                                      const totalFrom = Object.values(tos).reduce((sum, v) => sum + v.count, 0);
                                      const isCurrentRow = fr === t.current_value;
                                      return (
                                        <tr key={fr} className={isCurrentRow ? 'bg-purple-50/50' : ''}>
                                          <td className={`px-3 py-1.5 font-bold border-b text-gray-600 ${isCurrentRow ? 'text-purple-700' : ''}`}>
                                            {fr}
                                            {isCurrentRow && <span className="text-[9px] text-purple-400 ml-1">CUR</span>}
                                            <span className="text-[9px] text-gray-400 ml-1">({totalFrom}x)</span>
                                          </td>
                                          {oAllValues.map(to => {
                                            const cell = tos[to];
                                            if (!cell) return <td key={to} className="px-3 py-1.5 text-center border-b text-gray-200 text-[10px]">-</td>;
                                            const pct = cell.pct;
                                            const isHighest = pct === Math.max(...Object.values(tos).map(v => v.pct));
                                            return (
                                              <td key={to} className={`px-3 py-1.5 text-center border-b`}>
                                                <div className={`rounded px-1 py-0.5 ${
                                                  isHighest && pct >= 25 ? 'bg-blue-100 text-blue-800 font-bold' :
                                                  pct >= 20 ? 'bg-gray-100 text-gray-700' :
                                                  'text-gray-500'
                                                }`}>
                                                  <span className="text-[10px]">{cell.count}x</span>
                                                  <span className="text-[9px] text-gray-400 ml-0.5">({pct}%)</span>
                                                </div>
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}

                          {/* Visual flow from current value */}
                          {matrix[t.current_value] && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <p className="text-xs font-bold text-purple-700 mb-3 uppercase">
                                Next Draw Prediction: {key}={t.current_value} → ?
                              </p>
                              <div className="flex items-end gap-4">
                                {(() => {
                                  const cur2026 = matrix[t.current_value] || {};
                                  const curOverall = (t.overall_matrix || {})[t.current_value] || {};
                                  const allTos = [...new Set([...Object.keys(cur2026), ...Object.keys(curOverall)])].sort((a, b) => Number(a) - Number(b));
                                  return allTos.map(to => {
                                    const v2026 = cur2026[to];
                                    const vAll = curOverall[to];
                                    const pct2026 = v2026?.pct || 0;
                                    const pctAll = vAll?.pct || 0;
                                    const barH2026 = Math.max(pct2026 * 1.2, 4);
                                    const barHAll = Math.max(pctAll * 1.2, 4);
                                    return (
                                      <div key={to} className="flex flex-col items-center">
                                        <div className="flex gap-1 items-end">
                                          <div className="flex flex-col items-center">
                                            <span className="text-[9px] font-bold text-purple-600 mb-0.5">{pct2026}%</span>
                                            <div className="w-7 bg-purple-500 rounded-t" style={{ height: `${barH2026}px` }} title={`2026: ${v2026?.count || 0}x (${pct2026}%)`} />
                                          </div>
                                          <div className="flex flex-col items-center">
                                            <span className="text-[9px] font-bold text-gray-400 mb-0.5">{pctAll}%</span>
                                            <div className="w-7 bg-gray-300 rounded-t" style={{ height: `${barHAll}px` }} title={`Overall: ${vAll?.count || 0}x (${pctAll}%)`} />
                                          </div>
                                        </div>
                                        <div className="w-16 bg-gray-200 text-center py-1 rounded-b-lg">
                                          <span className="font-bold text-sm text-gray-800">→ {to}</span>
                                        </div>
                                        <div className="text-[9px] mt-0.5">
                                          <span className="text-purple-600">{v2026?.count || 0}x</span>
                                          <span className="text-gray-400 ml-1">{vAll?.count || 0}x</span>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                              <div className="flex gap-4 mt-3 text-[10px]">
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-purple-500" /> 2026</span>
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-300" /> Overall</span>
                              </div>
                            </div>
                          )}

                          {/* Timeline: sequence of values in 2026 */}
                          <div className="mt-4">
                            <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">{key} value per draw in 2026 (left = oldest)</p>
                            <div className="flex gap-1 flex-wrap">
                              {(t.counts_per_draw || []).map((v, i) => (
                                <span key={i} className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-bold font-mono ${
                                  i === t.counts_per_draw.length - 1
                                    ? 'bg-purple-600 text-white ring-2 ring-purple-300'
                                    : v === 0 ? 'bg-gray-100 text-gray-400'
                                    : v === 1 ? 'bg-blue-100 text-blue-700'
                                    : v === 2 ? 'bg-amber-100 text-amber-700'
                                    : v >= 3 ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}>{v}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="yearly-matrix">
              <YearlyMatrixTab />
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}

/* ============ Yearly Matrix (per ball × per range × per year) ============ */
function YearlyMatrixTab() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBall, setActiveBall] = useState('ball_1');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analysis/range-bias/yearly-matrix`)
      .then(r => setMatrix(r.data))
      .catch(() => toast.error('Failed to load yearly matrix'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-10"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div>;
  if (!matrix || !matrix.balls) return <div className="text-gray-500 p-6">No data</div>;

  const years = matrix.years || [];
  const currentBall = matrix.balls.find(b => b.ball === activeBall) || matrix.balls[0];

  // Heatmap helper — colour intensity per cell based on max in row
  const cellShade = (count, maxInRow) => {
    if (count === 0) return 'bg-gray-50 text-gray-300';
    if (maxInRow === 0) return 'bg-gray-50 text-gray-400';
    const ratio = count / maxInRow;
    if (ratio >= 0.85) return 'bg-emerald-500 text-white font-bold';
    if (ratio >= 0.65) return 'bg-emerald-300 text-emerald-900 font-bold';
    if (ratio >= 0.45) return 'bg-emerald-100 text-emerald-800';
    if (ratio >= 0.25) return 'bg-amber-50 text-amber-700';
    return 'bg-gray-50 text-gray-600';
  };

  const exportCsv = () => {
    const lines = [];
    lines.push(`Ball,${currentBall.label}`);
    const yr2026 = years[0];
    const restYears = years.slice(1);
    for (const rng of currentBall.ranges) {
      lines.push('');
      lines.push(`Range ${rng.range}`);
      lines.push(['Number', yr2026 || '', `${yr2026 || ''} %`, 'Lifetime %', 'Grand Total', ...restYears].join(','));
      for (const row of rng.rows) {
        const cnt2026 = yr2026 ? (row.by_year[yr2026] || 0) : 0;
        const total2026 = yr2026 ? (rng.year_totals[yr2026] || 0) : 0;
        const pct2026 = total2026 > 0 ? ((cnt2026 / total2026) * 100).toFixed(1) : '0.0';
        const pctLife = rng.grand_total > 0 ? ((row.total / rng.grand_total) * 100).toFixed(1) : '0.0';
        lines.push([row.number, cnt2026, `${pct2026}%`, `${pctLife}%`, row.total, ...restYears.map(y => row.by_year[y] || 0)].join(','));
      }
      lines.push(['Range Total', rng.year_totals[yr2026] || 0, '100%', '100%', rng.grand_total, ...restYears.map(y => rng.year_totals[y] || 0)].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `w24_yearly_matrix_${currentBall.ball}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="yearly-matrix-tab">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#002FA7] to-indigo-600 rounded-lg px-5 py-3 text-white flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Per-Ball × Per-Range × Per-Year Frequency Matrix</h3>
          <p className="text-[11px] text-white/85">For each ball position, every number 1–50 split by range × calendar year. {matrix.total_draws} total draws across {years.length} years.</p>
        </div>
        <Button onClick={exportCsv} className="bg-white text-[#002FA7] hover:bg-amber-50 font-bold text-xs h-8" data-testid="ym-export-btn">Export CSV ({currentBall.label})</Button>
      </div>

      {/* Ball selector */}
      <div className="flex gap-2 flex-wrap" data-testid="ym-ball-tabs">
        {matrix.balls.map(b => (
          <button
            key={b.ball}
            onClick={() => setActiveBall(b.ball)}
            data-testid={`ym-ball-${b.ball}`}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
              activeBall === b.ball
                ? 'bg-[#002FA7] text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Year-draw-count strip */}
      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-800 flex flex-wrap gap-3">
        <span className="font-bold uppercase tracking-wider">Draws per year:</span>
        {years.map(y => (
          <span key={y} className="font-mono"><b>{y}</b>: {matrix.year_draw_counts[y]}</span>
        ))}
      </div>

      {/* Per-ball Summary — Till 2025 vs 2026 by Range */}
      {(() => {
        const yr2026 = years[0];
        const otherYears = years.slice(1);
        const numOther = otherYears.length || 1;

        // Compute per-range Till-2025 and 2026 counts
        const rows = currentBall.ranges.map(rng => {
          const cnt2026 = rng.year_totals[yr2026] || 0;
          const till2025 = rng.grand_total - cnt2026;
          return {
            range: rng.range,
            till2025,
            avg_per_year: till2025 / numOther,
            cnt2026,
          };
        });
        const ballTill2025 = rows.reduce((s, r) => s + r.till2025, 0);
        const ballCnt2026 = rows.reduce((s, r) => s + r.cnt2026, 0);
        const ballAvgPerYear = ballTill2025 / numOther;

        return (
          <div className="bg-white border-2 border-[#002FA7] rounded-lg overflow-hidden shadow-md" data-testid={`ym-summary-${currentBall.ball}`}>
            <div className="bg-gradient-to-r from-[#002FA7] to-indigo-700 text-white px-4 py-2.5">
              <h4 className="font-bold text-sm flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 bg-amber-400 text-slate-900 rounded text-[10px] font-black">SUMMARY</span>
                {currentBall.label} — Till 2025 vs 2026 by Range
              </h4>
              <p className="text-[10px] text-white/85 mt-0.5">
                Compares each range's lifetime share (till 2025) against its 2026 share. Negative Diff (red) = range running HOT in 2026; positive Diff (green) = range running COLD.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={`ym-summary-table-${currentBall.ball}`}>
                <thead className="bg-gray-100">
                  <tr className="text-[11px] text-gray-700">
                    <th className="px-3 py-2 text-left font-black border-r border-gray-300">{currentBall.label}</th>
                    <th className="px-3 py-2 text-center font-bold bg-blue-50">Till 2025</th>
                    <th className="px-3 py-2 text-center font-bold bg-blue-50">Avg / year</th>
                    <th className="px-3 py-2 text-center font-bold bg-blue-50">%</th>
                    <th className="px-3 py-2 text-center font-bold bg-amber-100">2026</th>
                    <th className="px-3 py-2 text-center font-bold bg-amber-100">%</th>
                    <th className="px-3 py-2 text-center font-black bg-slate-100 border-l-2 border-slate-300">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const pctLife = ballTill2025 > 0 ? (r.till2025 / ballTill2025 * 100) : 0;
                    const pct2026 = ballCnt2026 > 0 ? (r.cnt2026 / ballCnt2026 * 100) : 0;
                    const diff = pctLife - pct2026;
                    // SWAPPED COLOR LOGIC:
                    //  + diff = 2026 is BELOW lifetime average (range UNDER-performing → LOSER) → RED
                    //  - diff = 2026 is ABOVE lifetime average (range OVER-performing → WINNER) → GREEN
                    const diffColor = diff > 1.5 ? 'bg-red-500 text-white font-black' : diff < -1.5 ? 'bg-emerald-500 text-white font-black' : 'bg-gray-100 text-gray-700 font-bold';
                    return (
                      <tr key={r.range} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`ym-summary-row-${r.range}`}>
                        <td className="px-3 py-2 font-mono font-black text-base border-r border-gray-200">{r.range}</td>
                        <td className="px-3 py-2 text-center font-mono font-bold bg-blue-50/40">{r.till2025}</td>
                        <td className="px-3 py-2 text-center font-mono bg-blue-50/40">{r.avg_per_year.toFixed(1)}</td>
                        <td className="px-3 py-2 text-center font-mono font-bold bg-blue-50/40">{pctLife.toFixed(1)}%</td>
                        <td className="px-3 py-2 text-center font-mono font-bold bg-amber-100/60">{r.cnt2026}</td>
                        <td className="px-3 py-2 text-center font-mono font-bold bg-amber-100/60">{pct2026.toFixed(1)}%</td>
                        <td className={`px-3 py-2 text-center font-mono border-l-2 border-slate-300 ${diffColor}`}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr className="bg-slate-200 border-t-2 border-slate-400 font-black">
                    <td className="px-3 py-2 text-left font-mono text-slate-800 border-r border-slate-300">TOTAL</td>
                    <td className="px-3 py-2 text-center font-mono text-slate-900 bg-blue-100">{ballTill2025}</td>
                    <td className="px-3 py-2 text-center font-mono text-slate-900 bg-blue-100">{ballAvgPerYear.toFixed(1)}</td>
                    <td className="px-3 py-2 text-center font-mono text-blue-800 bg-blue-100">100%</td>
                    <td className="px-3 py-2 text-center font-mono text-slate-900 bg-amber-200">{ballCnt2026}</td>
                    <td className="px-3 py-2 text-center font-mono text-amber-800 bg-amber-200">100%</td>
                    <td className="px-3 py-2 text-center font-mono bg-slate-300 border-l-2 border-slate-400">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 border-t border-blue-200 px-4 py-2 text-[10px] text-blue-800">
              <b>Till 2025</b> = appearances in years before 2026 ({numOther} year{numOther !== 1 ? 's' : ''}). <b>Avg / year</b> = Till 2025 ÷ {numOther}. <b>Diff</b> = Lifetime % − 2026 %.{' '}
              <span className="text-red-700 font-bold">Red Diff (+)</span> = range UNDER-performing in 2026 (running cold / loser; due for catch-up).{' '}
              <span className="text-emerald-700 font-bold">Green Diff (−)</span> = range OVER-performing in 2026 (running hot / winner).
            </div>
          </div>
        );
      })()}

      {/* Loser Analysis — biggest under-performer for current ball + across all balls */}
      {(() => {
        const yr2026 = years[0];
        const otherYears = years.slice(1);
        const numOther = otherYears.length || 1;

        // For each ball, compute per-range diffs
        const perBall = matrix.balls.map(b => {
          const rngRows = b.ranges.map(rng => {
            const cnt26 = rng.year_totals[yr2026] || 0;
            const till25 = rng.grand_total - cnt26;
            return { range: rng.range, till25, cnt26 };
          });
          const ballTill = rngRows.reduce((s, r) => s + r.till25, 0);
          const ballCnt26 = rngRows.reduce((s, r) => s + r.cnt26, 0);
          const enriched = rngRows.map(r => {
            const pctLife = ballTill > 0 ? (r.till25 / ballTill * 100) : 0;
            const pct26 = ballCnt26 > 0 ? (r.cnt26 / ballCnt26 * 100) : 0;
            return { ...r, pctLife, pct26, diff: pctLife - pct26 };
          });
          return { ball: b.ball, label: b.label, ranges: enriched };
        });

        const cur = perBall.find(b => b.ball === activeBall);
        const curBiggestLoser = cur ? [...cur.ranges].sort((a, b) => b.diff - a.diff)[0] : null;
        const curBiggestWinner = cur ? [...cur.ranges].sort((a, b) => a.diff - b.diff)[0] : null;

        // Aggregate across all balls — average diff per range
        const aggMap = {};
        for (const b of perBall) {
          for (const r of b.ranges) {
            if (!aggMap[r.range]) aggMap[r.range] = { range: r.range, diffs: [], till25: 0, cnt26: 0 };
            aggMap[r.range].diffs.push(r.diff);
            aggMap[r.range].till25 += r.till25;
            aggMap[r.range].cnt26 += r.cnt26;
          }
        }
        const totalTill25Agg = Object.values(aggMap).reduce((s, x) => s + x.till25, 0);
        const totalCnt26Agg = Object.values(aggMap).reduce((s, x) => s + x.cnt26, 0);
        const aggregated = Object.values(aggMap).map(x => {
          const avgDiff = x.diffs.reduce((s, d) => s + d, 0) / x.diffs.length;
          const aggLife = totalTill25Agg > 0 ? (x.till25 / totalTill25Agg * 100) : 0;
          const agg26 = totalCnt26Agg > 0 ? (x.cnt26 / totalCnt26Agg * 100) : 0;
          const ballsLagging = x.diffs.filter(d => d > 1.5).length;
          return { range: x.range, avg_diff: avgDiff, agg_diff: aggLife - agg26, agg_life: aggLife, agg_2026: agg26, balls_lagging: ballsLagging };
        }).sort((a, b) => b.agg_diff - a.agg_diff);

        const biggestLoserOverall = aggregated[0];
        const biggestWinnerOverall = aggregated[aggregated.length - 1];

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ym-loser-analysis">
            {/* Current ball summary */}
            <div className="bg-gradient-to-br from-rose-50 to-red-50 border-2 border-rose-300 rounded-lg p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 mb-2">{currentBall.label} — Range Performance</div>
              {curBiggestLoser && curBiggestLoser.diff > 0 && (
                <div className="bg-white rounded-lg p-3 border-2 border-red-400 shadow-sm mb-2" data-testid="ym-biggest-loser">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-red-700 font-bold uppercase tracking-wider">⬇ Biggest Loser (cold)</div>
                      <div className="text-2xl font-mono font-black text-red-600 mt-1">Range {curBiggestLoser.range}</div>
                      <div className="text-[11px] text-gray-600 mt-1">Lifetime <b>{curBiggestLoser.pctLife.toFixed(1)}%</b> → 2026 <b>{curBiggestLoser.pct26.toFixed(1)}%</b></div>
                    </div>
                    <div className="text-3xl font-mono font-black text-red-600">+{curBiggestLoser.diff.toFixed(1)}%</div>
                  </div>
                  <div className="text-[10px] text-red-700 mt-2 italic">Under-performing → due for catch-up. {curBiggestLoser.cnt26} hits in 2026 vs lifetime avg of <b>{(curBiggestLoser.till25 / numOther).toFixed(1)}/yr</b>.</div>
                </div>
              )}
              {curBiggestWinner && curBiggestWinner.diff < 0 && (
                <div className="bg-white rounded-lg p-3 border-2 border-emerald-400 shadow-sm" data-testid="ym-biggest-winner">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">⬆ Biggest Winner (hot)</div>
                      <div className="text-2xl font-mono font-black text-emerald-600 mt-1">Range {curBiggestWinner.range}</div>
                      <div className="text-[11px] text-gray-600 mt-1">Lifetime <b>{curBiggestWinner.pctLife.toFixed(1)}%</b> → 2026 <b>{curBiggestWinner.pct26.toFixed(1)}%</b></div>
                    </div>
                    <div className="text-3xl font-mono font-black text-emerald-600">{curBiggestWinner.diff.toFixed(1)}%</div>
                  </div>
                  <div className="text-[10px] text-emerald-700 mt-2 italic">Over-performing in 2026 → may regress.</div>
                </div>
              )}
            </div>

            {/* Aggregated across all balls */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-300 rounded-lg p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 mb-2">Across All 5 Balls — Range Performance</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="ym-agg-table">
                  <thead className="bg-indigo-100">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-bold text-indigo-800">Range</th>
                      <th className="px-2 py-1.5 text-center font-bold text-indigo-800">Lifetime %</th>
                      <th className="px-2 py-1.5 text-center font-bold text-indigo-800">2026 %</th>
                      <th className="px-2 py-1.5 text-center font-bold text-indigo-800">Diff</th>
                      <th className="px-2 py-1.5 text-center font-bold text-indigo-800">Balls Lagging</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregated.map(r => {
                      const isLoserOverall = r.range === biggestLoserOverall.range && r.agg_diff > 0;
                      const isWinnerOverall = r.range === biggestWinnerOverall.range && r.agg_diff < 0;
                      const diffColor = r.agg_diff > 1.5 ? 'bg-red-500 text-white font-black' : r.agg_diff < -1.5 ? 'bg-emerald-500 text-white font-black' : 'bg-gray-100 text-gray-700 font-bold';
                      return (
                        <tr key={r.range} className={`border-b border-indigo-100 ${isLoserOverall ? 'bg-red-50' : isWinnerOverall ? 'bg-emerald-50' : 'bg-white'}`} data-testid={`ym-agg-row-${r.range}`}>
                          <td className="px-2 py-1.5 font-mono font-black text-indigo-900">
                            {r.range}
                            {isLoserOverall && <span className="ml-1 text-[9px] font-bold text-red-700">⬇ LOSER</span>}
                            {isWinnerOverall && <span className="ml-1 text-[9px] font-bold text-emerald-700">⬆ HOT</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center font-mono">{r.agg_life.toFixed(1)}%</td>
                          <td className="px-2 py-1.5 text-center font-mono">{r.agg_2026.toFixed(1)}%</td>
                          <td className={`px-2 py-1.5 text-center font-mono ${diffColor}`}>
                            {r.agg_diff > 0 ? '+' : ''}{r.agg_diff.toFixed(1)}%
                          </td>
                          <td className="px-2 py-1.5 text-center font-mono">
                            <span className={`inline-block px-2 py-0.5 rounded font-bold ${r.balls_lagging >= 4 ? 'bg-red-100 text-red-700' : r.balls_lagging >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                              {r.balls_lagging} / 5
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {biggestLoserOverall && biggestLoserOverall.agg_diff > 0 && (
                <div className="mt-3 bg-white rounded p-2 border border-red-200 text-[10px] text-red-800">
                  <b>Verdict:</b> Range <b className="text-red-600 text-base font-mono">{biggestLoserOverall.range}</b> is the most lagging cluster in 2026 — across <b>{biggestLoserOverall.balls_lagging} of 5</b> ball positions it is under-represented vs lifetime norm. Aggregate deficit: <b>+{biggestLoserOverall.agg_diff.toFixed(1)}%</b>.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Per-range tables */}
      {currentBall.ranges.map(rng => {
        // Compute per-row max for heatmap shading
        return (
          <div key={rng.range} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`ym-range-${rng.range}`}>
            <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
              <h4 className="font-bold text-sm">
                <span className="inline-block px-2 py-0.5 bg-amber-400 text-slate-900 rounded text-[10px] font-black mr-2">Range {rng.range}</span>
                {currentBall.label} — Yearly Distribution
              </h4>
              <span className="text-[11px] font-mono text-amber-300">Grand Total: {rng.grand_total}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid={`ym-table-${rng.range}`}>
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-center font-black text-gray-700 sticky left-0 bg-gray-100 border-r border-gray-300">#</th>
                    {years.length > 0 && (
                      <th className="px-2 py-2 text-center font-bold text-gray-600 min-w-[48px] bg-amber-50/50">{years[0]}</th>
                    )}
                    <th className="px-2 py-2 text-center font-black text-amber-700 bg-amber-100 min-w-[56px]">{years[0] || ''} %</th>
                    <th className="px-2 py-2 text-center font-black text-emerald-700 bg-emerald-100 min-w-[56px]">Lifetime %</th>
                    <th className="px-3 py-2 text-center font-black text-amber-800 bg-amber-200 border-l-2 border-r-2 border-amber-400 min-w-[64px]">Grand Total</th>
                    {years.slice(1).map(y => (
                      <th key={y} className="px-2 py-2 text-center font-bold text-gray-600 min-w-[48px]">{y}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rng.rows.map(row => {
                    const maxInRow = Math.max(...years.map(y => row.by_year[y] || 0), 1);
                    const yr2026 = years[0];
                    const cnt2026 = yr2026 ? (row.by_year[yr2026] || 0) : 0;
                    const total2026 = yr2026 ? (rng.year_totals[yr2026] || 0) : 0;
                    const pct2026 = total2026 > 0 ? (cnt2026 / total2026 * 100) : 0;
                    const pctLifetime = rng.grand_total > 0 ? (row.total / rng.grand_total * 100) : 0;
                    const diffPct = pct2026 - pctLifetime;
                    const pct2026Color = diffPct > 1.5 ? 'text-emerald-700 bg-emerald-50' : diffPct < -1.5 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-amber-50/40';
                    return (
                      <tr key={row.number} className="border-b border-gray-100">
                        <td className="px-2 py-1.5 text-center font-mono font-black text-[#002FA7] sticky left-0 bg-white border-r border-gray-200">{row.number}</td>
                        {/* 2026 count */}
                        <td className={`px-2 py-1.5 text-center font-mono text-[11px] ${cellShade(cnt2026, maxInRow)}`}>
                          {cnt2026 > 0 ? cnt2026 : '·'}
                        </td>
                        {/* 2026 % */}
                        <td className={`px-2 py-1.5 text-center font-mono font-bold ${pct2026Color}`} title={`${cnt2026}/${total2026}`}>
                          {pct2026.toFixed(1)}%
                        </td>
                        {/* Lifetime % */}
                        <td className="px-2 py-1.5 text-center font-mono font-bold text-emerald-700 bg-emerald-50/60" title={`${row.total}/${rng.grand_total}`}>
                          {pctLifetime.toFixed(1)}%
                        </td>
                        {/* Grand Total */}
                        <td className="px-3 py-1.5 text-center font-mono font-black bg-amber-50 text-amber-900 border-l-2 border-r-2 border-amber-200">{row.total}</td>
                        {/* Other years (2025..2004) */}
                        {years.slice(1).map(y => {
                          const cnt = row.by_year[y] || 0;
                          return (
                            <td key={y} className={`px-2 py-1.5 text-center font-mono text-[11px] ${cellShade(cnt, maxInRow)}`}>
                              {cnt > 0 ? cnt : '·'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* Year totals footer */}
                  <tr className="bg-slate-100 border-t-2 border-slate-300">
                    <td className="px-2 py-2 text-center font-black text-slate-700 sticky left-0 bg-slate-100 border-r border-slate-300">Σ</td>
                    {years.length > 0 && (
                      <td className="px-2 py-2 text-center font-mono font-bold text-slate-700 bg-amber-50/40">{rng.year_totals[years[0]] || 0}</td>
                    )}
                    <td className="px-2 py-2 text-center font-mono font-black text-amber-800 bg-amber-100">100%</td>
                    <td className="px-2 py-2 text-center font-mono font-black text-emerald-800 bg-emerald-100">100%</td>
                    <td className="px-3 py-2 text-center font-mono font-black bg-amber-200 text-amber-900 border-l-2 border-r-2 border-amber-400">{rng.grand_total}</td>
                    {years.slice(1).map(y => (
                      <td key={y} className="px-2 py-2 text-center font-mono font-bold text-slate-700">{rng.year_totals[y] || 0}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-800">
        <b>Reading:</b> Order — Number → 2026 count → <b className="text-amber-700">2026 %</b> (this number's share of the range in 2026) → <b className="text-emerald-700">Lifetime %</b> (this number's share of the range across all years) → <b>Grand Total</b> → 2025…2004. Compare 2026 % vs Lifetime % to spot numbers running hot/cold this year. Green-shaded 2026% = above lifetime average; red-shaded = below.
      </div>
    </div>
  );
}


/* ============ Convergence Tab — F1/F2/Convergence + Per-Ball + Per-Cell ============ */
const RANGE_LABELS = ['L', 'ML', 'M', 'MH', 'H'];
const RANGE_BOUNDS = { L: '1-10', ML: '11-20', M: '21-30', MH: '31-40', H: '41-50' };

const TIER_BADGE = {
  ELITE: 'bg-amber-300 text-amber-950 ring-amber-400 ring-2',
  STRONG: 'bg-emerald-200 text-emerald-900 ring-emerald-300 ring-1',
  MODERATE: 'bg-gray-200 text-gray-700 ring-gray-300 ring-1',
};

function W24ConvergenceTab({ data }) {
  const pc = data?.pattern_convergence || {};
  const prf = data?.pattern_risk_flags || {};
  const pbc = data?.per_ball_convergence || {};
  const cells = data?.per_cell_gaps || {};
  const f1Table = data?.f1_table || [];
  const f2Table = data?.f2_table || [];
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  const renderRiskChip = (pat) => {
    const info = prf[pat];
    if (!info || !info.flags?.length) return null;
    const sev = info.severity === 'high' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-700 border-amber-300';
    const tooltip = info.labels.join(' · ');
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ml-1 rounded text-[9px] font-bold border ${sev}`} title={tooltip} data-testid={`risk-${pat}`}>
        ⚠️ {info.flags.length}
      </span>
    );
  };

  return (
    <div className="space-y-5" data-testid="w24-convergence-tab">
      <BacktestPanel endpoint="range-pattern" />

      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 rounded-xl p-5 text-white shadow-xl">
        <h2 className="text-xl font-black mb-1">Pattern Convergence — Ball Position Distribution (1-1-1-1-1)</h2>
        <p className="text-[11px] text-emerald-100">Top range distribution patterns where Formula 1 (long-term overdue ^0.5) and Formula 2 (next-draw contextual / Markov / freq) agree. ELITE = top-3 in BOTH; STRONG = top-3 in one + top-5 in other; MODERATE = top-5 union.</p>
      </div>

      {/* Blind Spots banner — shows risk flags on top picks */}
      {Object.keys(prf).length > 0 && (() => {
        const topPicks = [...(pc.elite || []), ...(pc.strong || [])];
        const topPicksWithRisk = topPicks.filter(p => prf[p]);
        if (topPicksWithRisk.length === 0) return null;
        return (
          <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border-2 border-amber-300 rounded-xl p-4" data-testid="blind-spots-banner">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div className="flex-1">
                <h3 className="font-black text-amber-900 text-sm mb-1">Blind Spots Detected — Top Picks Have Weak Supporting Evidence</h3>
                <p className="text-[11px] text-amber-800 mb-3">These top-tier predictions converge on F1 ∩ F2 but carry counter-signals. Read the flags before acting on them.</p>
                <div className="space-y-2">
                  {topPicksWithRisk.map(pat => {
                    const info = prf[pat];
                    const tier = (pc.elite || []).includes(pat) ? 'ELITE' : 'STRONG';
                    return (
                      <div key={pat} className="bg-white border border-amber-200 rounded p-2.5 flex items-center gap-3" data-testid={`blind-spot-${pat}`}>
                        <span className={`px-2 py-1 rounded font-mono font-black text-base ${tier === 'ELITE' ? 'bg-amber-300 text-amber-950' : 'bg-emerald-200 text-emerald-900'}`}>{pat}</span>
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${tier === 'ELITE' ? 'bg-amber-200 text-amber-900' : 'bg-emerald-100 text-emerald-800'}`}>{tier === 'ELITE' ? '🥇 ELITE' : '🥈 STRONG'}</span>
                        <div className="flex flex-wrap gap-1.5 flex-1">
                          {info.labels.map(lbl => (
                            <span key={lbl} className="inline-flex items-center bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-0.5 text-[10px] font-bold">⚠️ {lbl}</span>
                          ))}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono whitespace-nowrap">2026: {info.count_2026}× · trans {info.trans_match_pct}% · ×{info.overdue_ratio} overdue</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pattern Convergence Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4" data-testid="pat-elite">
          <div className="text-xs font-black text-amber-900 mb-2 flex items-center gap-1.5">🥇 ELITE <span className="text-[10px] font-bold opacity-70">(F1 ∩ F2 top-3)</span></div>
          {(pc.elite || []).length === 0 ? (
            <div className="text-[11px] text-amber-700 italic">No ELITE convergence this draw — F1 and F2 disagree at top-3.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(pc.elite || []).map(p => (
                <span key={p} className={`inline-flex items-center px-3 py-1.5 rounded font-mono font-black text-base shadow ${TIER_BADGE.ELITE}`}>{p}{renderRiskChip(p)}</span>
              ))}
            </div>
          )}
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4" data-testid="pat-strong">
          <div className="text-xs font-black text-emerald-900 mb-2">🥈 STRONG</div>
          {(pc.strong || []).length === 0 ? (
            <div className="text-[11px] text-emerald-700 italic">—</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(pc.strong || []).map(p => (
                <span key={p} className={`inline-flex items-center px-2.5 py-1 rounded font-mono font-bold text-sm ${TIER_BADGE.STRONG}`}>{p}{renderRiskChip(p)}</span>
              ))}
            </div>
          )}
        </div>
        <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-4" data-testid="pat-moderate">
          <div className="text-xs font-black text-gray-700 mb-2">🥉 MODERATE</div>
          {(pc.moderate || []).length === 0 ? (
            <div className="text-[11px] text-gray-500 italic">—</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(pc.moderate || []).map(p => (
                <span key={p} className={`inline-flex items-center px-2 py-0.5 rounded font-mono text-xs ${TIER_BADGE.MODERATE}`}>{p}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* F1/F2 top-5 quick view */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-blue-50/40 rounded-lg p-3 border border-blue-200" data-testid="pat-f1-top5">
          <div className="font-bold text-blue-800 mb-2 text-sm">F1 Top-5 (Long-term · ^0.5 multiplicative)</div>
          <div className="flex flex-wrap gap-1.5">
            {(pc.f1_top5 || []).map((p, i) => (
              <span key={p} className={`inline-flex items-center px-2 py-1 rounded font-mono text-sm ${i < 3 ? 'bg-blue-600 text-white font-bold' : 'bg-gray-200 text-gray-700'}`}>{p}</span>
            ))}
          </div>
        </div>
        <div className="bg-orange-50/40 rounded-lg p-3 border border-orange-200" data-testid="pat-f2-top5">
          <div className="font-bold text-orange-800 mb-2 text-sm">F2 Top-5 (Next-draw · additive)</div>
          <div className="flex flex-wrap gap-1.5">
            {(pc.f2_top5 || []).map((p, i) => (
              <span key={p} className={`inline-flex items-center px-2 py-1 rounded font-mono text-sm ${i < 3 ? 'bg-orange-600 text-white font-bold' : 'bg-gray-200 text-gray-700'}`}>{p}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Per-Ball Range Convergence */}
      <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 shadow-sm">
        <h3 className="font-black text-emerald-900 mb-3">Per-Ball Range Convergence — best range per ball position</h3>
        <p className="text-[11px] text-gray-500 mb-3">For each ball: F1 = lifetime% × overdue^0.5. F2 = lifetime%·1.5 + overdue·10 + hot/cold bonus. ELITE = same range in F1 top-1 AND F2 top-1.</p>
        <div className="grid md:grid-cols-5 gap-3" data-testid="per-ball-convergence-grid">
          {ballKeys.map((bk, bi) => {
            const info = pbc[bk] || {};
            const eliteR = (info.elite || [])[0];
            const eliteCell = eliteR ? (info.scores || {})[eliteR] : null;
            return (
              <div key={bk} className="border-2 border-emerald-200 rounded-lg overflow-hidden" data-testid={`pball-${bi + 1}`}>
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2 border-b border-emerald-200">
                  <div className="text-xs font-black text-emerald-900">Ball {bi + 1}</div>
                </div>
                <div className="p-3 space-y-2">
                  {eliteR ? (
                    <div className="bg-amber-100 border-2 border-amber-400 rounded p-2" data-testid={`pball-${bi + 1}-elite`}>
                      <div className="text-[9px] font-black text-amber-900">🥇 ELITE</div>
                      <div className="font-mono font-black text-lg text-amber-950">{eliteR} <span className="text-[10px] font-normal text-amber-800">({RANGE_BOUNDS[eliteR]})</span></div>
                      {eliteCell && (
                        <div className="text-[10px] text-amber-900 leading-tight mt-1">
                          life {eliteCell.lifetime_pct}% · gap {eliteCell.current_gap}/{eliteCell.expected_gap} · ×{eliteCell.overdue_ratio} overdue
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[10px] text-gray-500 italic">No ELITE — F1/F2 disagree</div>
                  )}
                  {(info.strong || []).length > 0 && (
                    <div>
                      <div className="text-[9px] font-bold text-emerald-800">🥈 STRONG</div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {(info.strong || []).map(rl => (
                          <span key={rl} className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] bg-emerald-100 text-emerald-900">{rl}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(info.moderate || []).length > 0 && (
                    <div>
                      <div className="text-[9px] font-bold text-gray-600">🥉 MODERATE</div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {(info.moderate || []).map(rl => (
                          <span key={rl} className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] bg-gray-100 text-gray-700">{rl}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-Cell × Per-Range Gap Tracker */}
      <div className="bg-white border-2 border-purple-200 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2.5">
          <h3 className="font-black text-base">Per-Ball × Per-Range Gap & Return-Time Tracker</h3>
          <p className="text-[10px] text-white/85 mt-0.5">Each cell = (Ball, Range): expected gap (μ) vs current gap (g), overdue ratio g/μ, lifetime %, 2026 hot/cold delta. Status: 🟢 DUE (overdue≥2 + life≥30%), 🟡 WATCH (overdue≥1.3), ⚪ NEUTRAL, 🔵 COOLING (overdue&lt;0.7).</p>
        </div>
        <div className="overflow-x-auto" data-testid="per-cell-gap-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-black text-gray-700 border-r border-gray-300">Ball / Range</th>
                {RANGE_LABELS.map(rl => (
                  <th key={rl} className="px-3 py-2 text-center font-bold text-gray-700">
                    <div>{rl}</div>
                    <div className="text-[9px] font-normal text-gray-500">{RANGE_BOUNDS[rl]}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ballKeys.map((bk, bi) => (
                <tr key={bk} className={bi % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 font-black text-gray-800 border-r border-gray-300">Ball {bi + 1}</td>
                  {RANGE_LABELS.map(rl => {
                    const cell = cells[`${bk}|${rl}`];
                    if (!cell) return <td key={rl} className="px-2 py-2 text-center text-gray-300 italic">—</td>;
                    const status = cell.status;
                    const bg =
                      status === 'DUE' ? 'bg-emerald-100 border-emerald-300' :
                      status === 'WATCH' ? 'bg-amber-50 border-amber-200' :
                      status === 'COOLING' ? 'bg-blue-50 border-blue-200' :
                      'bg-gray-50 border-gray-200';
                    const dot =
                      status === 'DUE' ? '🟢' :
                      status === 'WATCH' ? '🟡' :
                      status === 'COOLING' ? '🔵' :
                      '⚪';
                    return (
                      <td key={rl} className={`px-2 py-1 border ${bg} text-center`} data-testid={`cell-${bi + 1}-${rl}`}>
                        <div className="font-mono font-bold text-sm text-gray-900">μ {cell.expected_gap} · g {cell.current_gap}</div>
                        <div className="font-mono text-[11px] text-gray-700">×{cell.overdue_ratio} overdue</div>
                        <div className="font-mono text-[10px] text-gray-600">life {cell.lifetime_pct}%</div>
                        <div className={`font-mono text-[10px] font-bold ${cell.hot_cold > 0 ? 'text-emerald-700' : cell.hot_cold < 0 ? 'text-red-600' : 'text-gray-500'}`}>2026 Δ {cell.hot_cold > 0 ? '+' : ''}{cell.hot_cold}%</div>
                        <div className="text-[10px] mt-0.5">{dot} <span className="font-bold">{status}</span></div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* F1 Patterns Table */}
      <div className="bg-white border-2 border-blue-200 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5">
          <h3 className="font-black text-base">Formula 1 (Long-term Overdue · ^0.5) — Top 30 Patterns</h3>
          <p className="text-[10px] text-white/85 mt-0.5">F1 = freq% · (gap/μ)^0.5 × volatility × recency × consec-miss boost</p>
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto" data-testid="f1-table">
          <PatternRankTable rows={f1Table} scoreKey="f1_score" />
        </div>
      </div>

      {/* F2 Patterns Table */}
      <div className="bg-white border-2 border-orange-200 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2.5">
          <h3 className="font-black text-base">Formula 2 (Next-draw Contextual · Additive) — Top 30 Patterns</h3>
          <p className="text-[10px] text-white/85 mt-0.5">F2 = trans×0.3 + min(overdue·10, 30) + freq%·1.5 + Markov-2nd × 0.2</p>
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto" data-testid="f2-table">
          <PatternRankTable rows={f2Table} scoreKey="f2_score" />
        </div>
      </div>
    </div>
  );
}

function PatternRankTable({ rows, scoreKey }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-gray-100 sticky top-0">
        <tr>
          <th className="px-2 py-1.5 text-left text-[10px] uppercase">#</th>
          <th className="px-3 py-1.5 text-left text-[10px] uppercase">Pattern</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">{scoreKey === 'f1_score' ? 'F1' : 'F2'}</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">Freq %</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">Gap</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">μ</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">×Overdue</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">2026</th>
          <th className="px-2 py-1.5 text-center text-[10px] uppercase">Tier</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.pattern} className={r.tier === 'ELITE' ? 'bg-amber-50' : r.tier === 'STRONG' ? 'bg-emerald-50/60' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={r.tier === 'ELITE' ? { borderLeft: '3px solid #f59e0b' } : r.tier === 'STRONG' ? { borderLeft: '3px solid #10b981' } : {}}>
            <td className="px-2 py-1 font-mono text-gray-400">{i + 1}</td>
            <td className="px-3 py-1 font-mono font-bold">{r.pattern}</td>
            <td className="px-2 py-1 font-mono font-bold text-center">{r[scoreKey]}</td>
            <td className="px-2 py-1 font-mono text-center">{r.freq_pct}%</td>
            <td className="px-2 py-1 font-mono text-center">{r.current_gap}</td>
            <td className="px-2 py-1 font-mono text-center">{r.expected_gap}</td>
            <td className={`px-2 py-1 font-mono text-center font-bold ${r.overdue_ratio >= 2 ? 'text-red-600' : r.overdue_ratio >= 1 ? 'text-amber-600' : 'text-gray-600'}`}>×{r.overdue_ratio}</td>
            <td className="px-2 py-1 text-center">
              {r.appeared_2026 ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">{r.count_2026}×</span> : <span className="text-[10px] text-gray-400">—</span>}
            </td>
            <td className="px-2 py-1 text-center">
              {r.tier ? (
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${r.tier === 'ELITE' ? 'bg-amber-300 text-amber-950' : r.tier === 'STRONG' ? 'bg-emerald-200 text-emerald-900' : 'bg-gray-200 text-gray-700'}`}>{r.tier === 'ELITE' ? '🥇' : r.tier === 'STRONG' ? '🥈' : '🥉'} {r.tier}</span>
              ) : <span className="text-[9px] text-gray-300">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
